import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useGetDropdownUsersQuery } from "../../api/addEmployeeApi";
import { useSubmitResignationMutation } from "../../api/resignationApi";
import { RESIGNATION_STATUS, apiErrorMessage, effectiveStatus } from "./constants";

const InitiateResignationDialog = ({ open, records, onClose, onSubmitted }) => {
  const { data: users = [], isLoading: usersLoading } = useGetDropdownUsersQuery(undefined, {
    skip: !open,
  });
  const [submitResignation, { isLoading }] = useSubmitResignationMutation();
  const [employeeId, setEmployeeId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setEmployeeId("");
      setReason("");
    }
  }, [open]);

  const busyIds = useMemo(
    () =>
      new Set(
        (records || [])
          .filter((record) => effectiveStatus(record) !== RESIGNATION_STATUS.IDLE)
          .map((record) => record.employeeId)
      ),
    [records]
  );
  const options = users.filter((user) => !busyIds.has(user.employeeId));

  if (!open) return null;

  const disabled = isLoading || !employeeId || !reason.trim();
  const selectedName = users.find((user) => user.employeeId === employeeId)?.name;

  const handleSubmit = async () => {
    try {
      await submitResignation({ employeeId, reason: reason.trim() }).unwrap();
      toast.success(`Resignation submitted for ${selectedName || "the employee"}.`);
      onSubmitted(employeeId);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not submit the resignation."));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-800">Initiate Resignation</h3>
        <p className="mt-2 text-sm text-gray-500">
          Submit a resignation on behalf of an employee. It goes into the queue as Pending
          and follows the normal approval flow.
        </p>

        <div className="mt-4">
          <label htmlFor="initiateEmployee" className="block text-sm font-medium text-gray-600">
            Employee <span className="text-red-500">*</span>
          </label>
          <select
            id="initiateEmployee"
            value={employeeId}
            onChange={(event) => setEmployeeId(event.target.value)}
            disabled={usersLoading}
            className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm outline-none focus:border-blue-primary"
          >
            <option value="" disabled>
              {usersLoading ? "Loading employees..." : "-- Select an employee --"}
            </option>
            {options.map((user) => (
              <option key={user.employeeId} value={user.employeeId}>
                {user.name}
              </option>
            ))}
          </select>
          {!usersLoading && busyIds.size > 0 && (
            <p className="mt-1 text-xs text-gray-400">
              Employees with a resignation already in progress are not listed.
            </p>
          )}
        </div>

        <div className="mt-4">
          <label htmlFor="initiateReason" className="block text-sm font-medium text-gray-600">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            id="initiateReason"
            rows={3}
            value={reason}
            placeholder="Reason given by the employee"
            onChange={(event) => setReason(event.target.value)}
            className="mt-1 w-full resize-none rounded-lg border border-gray-300 p-2 text-sm outline-none focus:border-blue-primary"
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled}
            className="rounded-lg bg-blue-primary px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {isLoading ? "Please wait..." : "Submit Resignation"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InitiateResignationDialog;
